import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { blogApi } from "../api/blogApi";
import { useAuth } from "../auth/authStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import MobileQuickNav from "../components/layout/MobileQuickNav";
import { getSiteOrigin } from "../utils/seo";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";

const PAGE_SIZE = 12;
const TOPIC_ALL = "Все";
const TOPICS = [TOPIC_ALL, "Тревожность", "Выгорание", "Отношения", "Самооценка", "Травма", "Родительство", "Для специалистов"];

const DEFAULT_POPULAR_AUTHORS = [
  { name: "Елизавета Деркач", initials: "ЕД", postsCount: 24, avatar: "linear-gradient(135deg,#5AEAA0,#A89DE8)", userId: null },
  { name: "Михаил Петров", initials: "МП", postsCount: 31, avatar: "linear-gradient(135deg,#5a3010,#d4a520)", userId: null },
  { name: "Наталья Воронова", initials: "НВ", postsCount: 22, avatar: "linear-gradient(135deg,#72243e,#ed93b1)", userId: null },
  { name: "Анна Соколова", initials: "АС", postsCount: 18, avatar: "linear-gradient(135deg,#085041,#5DCAA5)", userId: null },
];

const CATEGORY_VISUALS = {
  Тревожность: { tagClass: "tag-lav", cardClass: "cc-lav", emoji: "🧠", coverAccent: "Тревожность" },
  Выгорание: { tagClass: "tag-amber", cardClass: "cc-amber", emoji: "🔥", coverAccent: "Выгорание" },
  Отношения: { tagClass: "tag-pink", cardClass: "cc-pink", emoji: "💬", coverAccent: "Отношения" },
  Самооценка: { tagClass: "tag-lav", cardClass: "cc-lav", emoji: "🧩", coverAccent: "Самооценка" },
  Травма: { tagClass: "tag-mint", cardClass: "cc-teal", emoji: "🌱", coverAccent: "Травма" },
  Родительство: { tagClass: "tag-amber", cardClass: "cc-amber", emoji: "🌙", coverAccent: "Родительство" },
  "Для специалистов": { tagClass: "tag-grey", cardClass: "cc-coral", emoji: "🎓", coverAccent: "Для специалистов" },
};

const FALLBACK_VISUALS = [
  { tagClass: "tag-lav", cardClass: "cc-lav", emoji: "✨", coverAccent: "Статья" },
  { tagClass: "tag-amber", cardClass: "cc-amber", emoji: "🌿", coverAccent: "Практика" },
  { tagClass: "tag-pink", cardClass: "cc-pink", emoji: "💡", coverAccent: "Разбор" },
  { tagClass: "tag-mint", cardClass: "cc-mint", emoji: "🧠", coverAccent: "Психология" },
  { tagClass: "tag-coral", cardClass: "cc-coral", emoji: "⚡", coverAccent: "Состояние" },
  { tagClass: "tag-grey", cardClass: "cc-teal", emoji: "🧘", coverAccent: "Практика" },
];

function formatDate(dt) {
  if (!dt) return "";
  return new Date(dt)
    .toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })
    .replace(" г.", "");
}

function buildPostPath(post) {
  if (!post) return "/blog";
  const id = post.id ?? post.postId ?? null;
  const slug = String(post.slug || "").trim();
  if (id && slug) return `/blog/${id}-${slug}`;
  if (slug) return `/blog/${slug}`;
  if (id) return `/blog/${id}`;
  return "/blog";
}

function getAuthorName(post) {
  return String(post?.author?.name || post?.authorName || "Специалист Бюро Души").trim();
}

function getAuthorUserId(post) {
  const raw = post?.author?.id ?? post?.author?.userId ?? post?.authorId ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function authorKey(author) {
  if (!author) return "author:unknown";
  if (author.userId) return `author:${author.userId}`;
  return `name:${String(author.name || "").trim().toLowerCase()}`;
}

function getInitials(name) {
  const words = String(name || "")
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean)
    .slice(0, 2);
  if (!words.length) return "БД";
  return words.map((w) => w[0]).join("").toUpperCase();
}

function getCategory(post) {
  return String(post?.category || post?.categoryTitle || "").trim();
}

function getVisual(post, idx = 0) {
  const category = getCategory(post);
  if (CATEGORY_VISUALS[category]) return CATEGORY_VISUALS[category];
  return FALLBACK_VISUALS[idx % FALLBACK_VISUALS.length];
}

function getReadingMinutes(post, fallback = 6) {
  const m = Number(post?.readingTimeMinutes || 0);
  if (Number.isFinite(m) && m > 0) return m;
  return fallback;
}

function getLikesCount(post, idx = 0) {
  const n = Number(post?.likesCount ?? post?.reactionsCount ?? post?.favoritesCount);
  if (Number.isFinite(n) && n >= 0) return n;
  return 120 + (idx % 6) * 27;
}

function getCommentsCount(post, idx = 0) {
  const n = Number(post?.commentsCount ?? post?.reviewsCount);
  if (Number.isFinite(n) && n >= 0) return n;
  return 14 + (idx % 6) * 6;
}

function getPostStatus(post = {}) {
  return String(post?.status || post?.publicationStatus || post?.state || post?.postStatus || "")
    .trim()
    .toUpperCase();
}

function isPublicPost(post) {
  const token = getPostStatus(post);
  if (!token) return true;
  return token === "PUBLISHED" || token === "PUBLIC" || token === "PUBLISH";
}

function isDraftPost(post) {
  return !isPublicPost(post);
}

export default function Blog() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, role, me } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [myPosts, setMyPosts] = React.useState([]);
  const q = (sp.get("q") || "").trim();
  const topicParam = (sp.get("topic") || TOPIC_ALL).trim();
  const activeTopic = TOPICS.includes(topicParam) ? topicParam : TOPIC_ALL;

  const [queryDraft, setQueryDraft] = React.useState(q);
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalElements, setTotalElements] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [myPostsLoading, setMyPostsLoading] = React.useState(false);

  const [likedMap, setLikedMap] = React.useState({});
  const [likeCountMap, setLikeCountMap] = React.useState({});
  const [likePendingMap, setLikePendingMap] = React.useState({});
  const [followedMap, setFollowedMap] = React.useState({});
  const [followPendingMap, setFollowPendingMap] = React.useState({});

  const [newsletterEmail, setNewsletterEmail] = React.useState("");
  const [toastText, setToastText] = React.useState("");
  const [toastShow, setToastShow] = React.useState(false);

  const toastTimerRef = React.useRef(null);
  const origin = React.useMemo(() => getSiteOrigin(), []);
  const roleToken = String(role || "").replace(/^ROLE_/i, "").toUpperCase();
  const canManageBlog = ["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(roleToken);
  const blogQuickNavItems = React.useMemo(() => [
    { to: "/blog", label: "Все статьи", icon: "☰" },
    canManageBlog ? { to: "/dashboard/blog", label: "Мои статьи", icon: "▦" } : null,
    canManageBlog ? { to: "/dashboard/blog/new", label: "Написать", icon: "+", accent: "create" } : null,
  ].filter(Boolean), [canManageBlog]);

  React.useEffect(() => {
    setQueryDraft(q);
  }, [q]);

  const notify = React.useCallback((text) => {
    setToastText(text);
    setToastShow(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastShow(false), 2200);
  }, []);

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const openPsychologistProfile = React.useCallback(
    (event, userId) => {
      const path = buildPsychologistProfilePath(userId);
      if (!path) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      navigate(path);
    },
    [navigate]
  );

  const loadPage = React.useCallback(
    async ({ nextPage = 0, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const response = await blogApi.list({
          page: nextPage,
          size: PAGE_SIZE,
          q,
          status: "PUBLISHED",
          sort: "publishedAt,desc",
        });

        const incomingRaw = Array.isArray(response?.content) ? response.content : [];
        const incoming = incomingRaw.filter(isPublicPost);

        setItems((prev) => {
          if (!append) return incoming;
          const map = new Map();
          [...prev, ...incoming].forEach((post, idx) => {
            const key = String(post?.id ?? post?.postId ?? post?.slug ?? `fallback-${idx}`);
            map.set(key, post);
          });
          return Array.from(map.values());
        });

        setPage(Number(response?.number ?? nextPage) || 0);
        setTotalPages(Math.max(1, Number(response?.totalPages ?? 1) || 1));
        setTotalElements(Number(response?.totalElements ?? 0) || 0);
      } catch (error) {
        console.error(error);
        if (!append) {
          setItems([]);
          setPage(0);
          setTotalPages(1);
          setTotalElements(0);
        }
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [q]
  );

  React.useEffect(() => {
    loadPage({ nextPage: 0, append: false });
  }, [loadPage]);

  React.useEffect(() => {
    setLikeCountMap((prev) => {
      const next = { ...prev };
      items.forEach((post, idx) => {
        const key = String(post?.id ?? post?.postId ?? post?.slug ?? `fallback-${idx}`);
        if (!(key in next)) next[key] = getLikesCount(post, idx + 1);
      });
      return next;
    });

    setLikedMap((prev) => {
      const next = { ...prev };
      items.forEach((post, idx) => {
        const key = String(post?.id ?? post?.postId ?? post?.slug ?? `fallback-${idx}`);
        if (!(key in next) && typeof post?.likedByMe === "boolean") {
          next[key] = Boolean(post.likedByMe);
        }
      });
      return next;
    });
  }, [items]);

  React.useEffect(() => {
    let mounted = true;
    if (!isAuthenticated) return () => { mounted = false; };

    (async () => {
      try {
        const ids = await blogApi.listAuthorSubscriptions();
        if (!mounted) return;
        const normalized = new Set((Array.isArray(ids) ? ids : []).map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0));
        setFollowedMap((prev) => {
          const next = { ...prev };
          normalized.forEach((id) => {
            next[`author:${id}`] = true;
          });
          return next;
        });
      } catch {
        // keep local state
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  React.useEffect(() => {
    let mounted = true;
    if (!isAuthenticated || !canManageBlog) {
      setMyPosts([]);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        setMyPostsLoading(true);
        const response = await blogApi.listMine({ page: 0, size: 12 });
        if (!mounted) return;
        const mine = Array.isArray(response?.content)
          ? response.content
          : Array.isArray(response?.items)
            ? response.items
            : [];
        setMyPosts(mine);
      } catch {
        if (!mounted) return;
        setMyPosts([]);
      } finally {
        if (mounted) setMyPostsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, canManageBlog]);

  const filteredItems = React.useMemo(() => {
    if (activeTopic === TOPIC_ALL) return items;
    return items.filter((post) => getCategory(post) === activeTopic);
  }, [items, activeTopic]);

  const featured = filteredItems[0] || null;
  const gridPosts = filteredItems.slice(1);
  const canLoadMore = page < totalPages - 1;

  const canonicalUrl = React.useMemo(() => {
    if (!origin) return "";
    const u = new URL("/blog", origin);
    if (q) u.searchParams.set("q", q);
    if (activeTopic !== TOPIC_ALL) u.searchParams.set("topic", activeTopic);
    return u.toString();
  }, [origin, q, activeTopic]);

  const robots = q || activeTopic !== TOPIC_ALL ? "noindex, follow" : "index, follow";

  const listSchemas = React.useMemo(() => {
    if (!origin) return [];
    const posts = (items || [])
      .slice(0, 20)
      .map((post) => ({
        "@type": "BlogPosting",
        headline: post?.title || "",
        url: post?.id && post?.slug ? `${origin}/blog/${post.id}-${post.slug}` : undefined,
        datePublished: post?.publishedAt || undefined,
        image: post?.previewImageUrl || undefined,
        description: post?.description || undefined,
      }))
      .filter((p) => p.url);

    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Блог",
        url: `${origin}/blog`,
        inLanguage: "ru-RU",
      },
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Блог",
        url: `${origin}/blog`,
        inLanguage: "ru-RU",
        blogPost: posts.length ? posts : undefined,
      },
    ];
  }, [origin, items]);

  const heroStats = React.useMemo(() => {
    const authors = new Set(items.map((post) => getAuthorName(post)).filter(Boolean));
    return {
      posts: (totalElements || items.length || 0).toLocaleString("ru-RU"),
      authors: authors.size || 47,
      readers: "38к",
    };
  }, [items, totalElements]);

  const myBlogStats = React.useMemo(() => {
    const published = myPosts.filter((post) => !isDraftPost(post)).length;
    const drafts = myPosts.filter((post) => isDraftPost(post)).length;
    return {
      total: myPosts.length,
      published,
      drafts,
    };
  }, [myPosts]);

  const popularAuthors = React.useMemo(() => {
    const map = new Map();
    items.forEach((post) => {
      const name = getAuthorName(post);
      if (!name) return;
      const userId = getAuthorUserId(post);
      const key = userId ? `author:${userId}` : `name:${name.toLowerCase()}`;
      if (!map.has(key)) map.set(key, { name, userId, postsCount: 0 });
      const current = map.get(key);
      map.set(key, { ...current, postsCount: (current?.postsCount || 0) + 1 });
    });

    const dynamic = Array.from(map.values())
      .map((entry, idx) => ({
        name: entry.name,
        userId: entry.userId,
        initials: getInitials(entry.name),
        postsCount: entry.postsCount,
        avatar: DEFAULT_POPULAR_AUTHORS[idx % DEFAULT_POPULAR_AUTHORS.length].avatar,
      }))
      .sort((a, b) => b.postsCount - a.postsCount)
      .slice(0, 4);

    if (dynamic.length >= 2) return dynamic;
    return DEFAULT_POPULAR_AUTHORS;
  }, [items]);

  const trendingTags = React.useMemo(() => {
    const tags = new Set();
    items.forEach((post) => {
      const cat = getCategory(post);
      if (cat) tags.add(`#${cat.toLowerCase()}`);
    });

    if (tags.size >= 6) return Array.from(tags).slice(0, 10);
    return [
      "#тревожность",
      "#выгорание",
      "#отношения",
      "#самооценка",
      "#травма",
      "#родительство",
      "#паника",
      "#кпт",
      "#психодрама",
      "#для специалистов",
    ];
  }, [items]);

  const applySearch = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(sp);
    const nextQ = queryDraft.trim();
    if (nextQ) next.set("q", nextQ);
    else next.delete("q");
    setSp(next);
  };

  const selectTopic = (topic) => {
    const next = new URLSearchParams(sp);
    if (!topic || topic === TOPIC_ALL) next.delete("topic");
    else next.set("topic", topic);
    setSp(next);
  };

  const onLoadMore = () => {
    if (!canLoadMore || loadingMore) return;
    loadPage({ nextPage: page + 1, append: true });
  };

  const onToggleLike = async (event, post, key, fallbackLikes = 0) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (likePendingMap[key]) return;

    const postId = Number(post?.id ?? post?.postId);
    if (!Number.isFinite(postId) || postId <= 0) {
      const wasLiked = Boolean(likedMap[key]);
      setLikedMap((prev) => ({ ...prev, [key]: !wasLiked }));
      setLikeCountMap((prev) => ({
        ...prev,
        [key]: Math.max(0, Number(prev[key] ?? fallbackLikes) + (wasLiked ? -1 : 1)),
      }));
      return;
    }

    try {
      setLikePendingMap((prev) => ({ ...prev, [key]: true }));
      const state = await blogApi.togglePostReaction(postId);
      if (state && typeof state === "object") {
        if (typeof state.liked === "boolean") {
          const wasLiked = Boolean(likedMap[key]);
          setLikedMap((prev) => ({ ...prev, [key]: state.liked }));
          if (!Number.isFinite(Number(state.reactionsCount))) {
            setLikeCountMap((prev) => ({
              ...prev,
              [key]: Math.max(0, Number(prev[key] ?? fallbackLikes) + (state.liked === wasLiked ? 0 : state.liked ? 1 : -1)),
            }));
          }
        }
        if (Number.isFinite(Number(state.reactionsCount))) {
          setLikeCountMap((prev) => ({ ...prev, [key]: Number(state.reactionsCount) }));
        }
      }
    } catch (error) {
      notify(error?.message || "Не удалось поставить реакцию");
    } finally {
      setLikePendingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const onToggleFollow = async (author) => {
    const key = authorKey(author);

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (followPendingMap[key]) return;

    if (!author?.userId) {
      let nextFollow = false;
      setFollowedMap((prev) => {
        nextFollow = !prev[key];
        return { ...prev, [key]: nextFollow };
      });
      notify(nextFollow ? "Вы подписались на автора" : "Подписка снята");
      return;
    }

    try {
      setFollowPendingMap((prev) => ({ ...prev, [key]: true }));
      const state = await blogApi.toggleAuthorSubscription(author.userId);
      if (state && typeof state === "object" && typeof state.isSubscribed === "boolean") {
        setFollowedMap((prev) => ({ ...prev, [key]: state.isSubscribed }));
        notify(state.isSubscribed ? "Вы подписались на автора" : "Подписка снята");
      } else {
        setFollowedMap((prev) => ({ ...prev, [key]: !prev[key] }));
      }
    } catch (error) {
      notify(error?.message || "Не удалось изменить подписку");
    } finally {
      setFollowPendingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const onNewsletterSubmit = (event) => {
    event.preventDefault();
    const email = newsletterEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      notify("Введите корректный email");
      return;
    }
    notify("Вы подписаны на рассылку");
    setNewsletterEmail("");
  };

  const onTrendTagClick = (tag) => {
    const raw = String(tag || "").trim().replace(/^#/, "");
    if (!raw) return;
    const match = TOPICS.find((topic) => topic.toLowerCase() === raw.toLowerCase());
    if (match) {
      selectTopic(match);
      return;
    }
    const next = new URLSearchParams(sp);
    next.delete("topic");
    next.set("q", raw);
    setSp(next);
  };

  const pageBody = (
    <>
      <section className="hero">
        <div className="hero-orb-a" />
        <div className="hero-orb-b" />

        <div className="hero-inner">
          <div className="hero-l">
            <div className="hero-badge">
              <div className="hero-badge-dot" />
              <div className="hero-badge-text">Блог платформы</div>
            </div>

            <h1 className="hero-title">
              Читай.
              <br />
              <span>Думай.</span>
              <br />
              Меняйся.
            </h1>

            <p className="hero-sub">
              Слова, которые задевают. Мысли, которые остаются. Психологи пишут честно — о том, что болит и что помогает.
            </p>

            <div className="hero-stats">
              <div className="hero-stat">
                <b>{heroStats.posts}</b>
                <span>публикаций</span>
              </div>
              <div className="hero-stat">
                <b>{heroStats.authors}</b>
                <span>авторов</span>
              </div>
              <div className="hero-stat">
                <b>{heroStats.readers}</b>
                <span>читателей</span>
              </div>
            </div>
          </div>

          <div className="hero-r">
            <form className="hero-search" onSubmit={applySearch}>
              <input
                value={queryDraft}
                onChange={(e) => setQueryDraft(e.target.value)}
                placeholder="Тема, автор, ключевое слово…"
              />
              <button className="hero-search-btn" type="submit">
                Найти →
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="page">
        {isAuthenticated ? (
          <>
            <div className="my-blog-banner">
              <div className="my-blog-icon">✍</div>
              <div className="my-blog-text">
                <div className="my-blog-title">
                  Ваш блог — {myPostsLoading ? "загрузка…" : `${myBlogStats.total} публикаций`}
                </div>
                <div className="my-blog-sub">
                  Опубликовано: {myBlogStats.published} · Черновиков: {myBlogStats.drafts}
                </div>
              </div>
              <div className="my-blog-actions">
                {canManageBlog ? (
                  <>
                    <button
                      className="podcasts-btn podcasts-btn--subtle blog-auth-topbar__create-btn"
                      type="button"
                      onClick={() => navigate("/dashboard/blog")}
                    >
                      Мои статьи
                    </button>
                    <button
                      className="podcasts-btn podcasts-btn--mint blog-auth-topbar__create-btn"
                      type="button"
                      onClick={() => navigate("/dashboard/blog/new")}
                    >
                      Написать
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            {canManageBlog ? (
              <div className="my-posts-strip">
                <div className="my-posts-header">
                  <div className="my-posts-label">Мои публикации</div>
                </div>
                <div className="my-post-cards">
                  {myPosts.slice(0, 8).map((post, idx) => {
                    const visual = getVisual(post, idx + 2);
                    const draft = isDraftPost(post);
                    const editId = encodeURIComponent(String(post?.id ?? post?.postId ?? post?.uuid ?? post?.slug ?? ""));
                    const targetPath = editId ? `/dashboard/blog/${editId}/edit` : buildPostPath(post);
                    return (
                      <Link key={`my-${post?.id || idx}`} className="my-post-card" to={targetPath}>
                        <div className={`mpc-cover ${visual.cardClass}`}>
                          {post?.previewImageUrl ? (
                            <img src={post.previewImageUrl} alt={post.title || "Обложка"} loading="lazy" decoding="async" />
                          ) : (
                            <span>{visual.emoji}</span>
                          )}
                        </div>
                        <div className="mpc-body">
                          <div className="mpc-title">{post?.title || "Без названия"}</div>
                          <div className="mpc-meta">
                            <span className={`mpc-status ${draft ? "status-draft" : "status-pub"}`}>
                              {draft ? "Черновик" : "Опубликовано"}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                  <button className="new-post-card" type="button" onClick={() => navigate("/dashboard/blog/new")}>
                    <span>+ Новая статья</span>
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="filters">
          {TOPICS.map((topic) => (
            <button
              key={topic}
              type="button"
              className={`fc${activeTopic === topic ? " on" : ""}`}
              onClick={() => selectTopic(topic)}
            >
              {topic}
            </button>
          ))}
        </div>

        <div className="sh">
          <div className="sh-title">Главное</div>
        </div>

        {loading ? (
          <div className="featured featured-skeleton" />
        ) : featured ? (
          <Link className="featured" to={buildPostPath(featured)}>
            <div className="feat-body">
              <div>
                <div className="feat-tags">
                  {getCategory(featured) ? (
                    <span className={`tag ${getVisual(featured, 0).tagClass}`}>{getCategory(featured)}</span>
                  ) : null}
                  <span className="tag tag-grey">Статья</span>
                </div>

                <div className="feat-title">{featured.title || "Без названия"}</div>
                <div className="feat-excerpt">
                  {featured.description ||
                    "Тревога о будущем — один из самых распространённых запросов. Разбираем механизм и практики, которые реально работают."}
                </div>

                <div className="feat-author">
                  {getAuthorUserId(featured) ? (
                    <button
                      type="button"
                      className="b-psych-link-btn"
                      onClick={(event) => openPsychologistProfile(event, getAuthorUserId(featured))}
                    >
                      <div className="feat-av">{getInitials(getAuthorName(featured))}</div>
                      <div>
                        <div className="feat-author-name">{getAuthorName(featured)}</div>
                        <div className="feat-author-spec">Клинический психолог · Психодрама</div>
                      </div>
                    </button>
                  ) : (
                    <>
                      <div className="feat-av">{getInitials(getAuthorName(featured))}</div>
                      <div>
                        <div className="feat-author-name">{getAuthorName(featured)}</div>
                        <div className="feat-author-spec">Клинический психолог · Психодрама</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="feat-meta">
                <span>{formatDate(featured.publishedAt || featured.createdWhen)}</span>
                <span>{getReadingMinutes(featured, 8)} мин чтения</span>
                <span>♡ {getLikesCount(featured, 1)}</span>
                <span>💬 {getCommentsCount(featured, 1)}</span>
                <div className="feat-read">
                  Читать
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="feat-cover">
              <div className="feat-cover-orb" />
              <div className="feat-cover-orb2" />
              {featured.previewImageUrl ? (
                <img className="feat-cover-img" src={featured.previewImageUrl} alt={featured.title || "Обложка статьи"} loading="lazy" decoding="async" />
              ) : (
                <div className="feat-cover-emoji">{getVisual(featured, 0).emoji}</div>
              )}
              <div className="feat-cover-pill">{getVisual(featured, 0).coverAccent}</div>
              <div className="feat-cover-time">⏱ {getReadingMinutes(featured, 8)} мин</div>
            </div>
          </Link>
        ) : (
          <div className="empty-state">Пока нет опубликованных статей.</div>
        )}

        <div className="sh">
          <div className="sh-title">Статьи</div>
          <div className="sh-more">Все статьи →</div>
        </div>

        {loading ? (
          <div className="grid3 grid3--skeleton">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="card card-skeleton" />
            ))}
          </div>
        ) : gridPosts.length ? (
          <div className="grid3">
            {gridPosts.map((post, idx) => {
              const visual = getVisual(post, idx + 1);
              const likeKey = String(post?.id ?? post?.postId ?? post?.slug ?? idx);
              const fallbackLikes = getLikesCount(post, idx + 1);
              const liked = !!likedMap[likeKey];
              const likes = Number.isFinite(Number(likeCountMap[likeKey]))
                ? Number(likeCountMap[likeKey])
                : fallbackLikes;

              return (
                <Link key={likeKey} className={`card ${visual.cardClass}`} to={buildPostPath(post)}>
                  <div className="card-cover">
                    {post.previewImageUrl ? (
                      <img src={post.previewImageUrl} alt={post.title || "Обложка статьи"} loading="lazy" decoding="async" />
                    ) : (
                      <div className="card-cover-emoji">{visual.emoji}</div>
                    )}

                    <div className="card-cover-author">
                      {getAuthorUserId(post) ? (
                        <button
                          type="button"
                          className="b-psych-link-btn"
                          onClick={(event) => openPsychologistProfile(event, getAuthorUserId(post))}
                        >
                          <div className="card-cover-dot">{getInitials(getAuthorName(post))}</div>
                          <span className="card-cover-name">{getAuthorName(post)}</span>
                        </button>
                      ) : (
                        <>
                          <div className="card-cover-dot">{getInitials(getAuthorName(post))}</div>
                          <span className="card-cover-name">{getAuthorName(post)}</span>
                        </>
                      )}
                    </div>

                    <div className="card-cover-time">{getReadingMinutes(post, 6)} мин</div>
                  </div>

                  <div className="card-body">
                    <div className="card-tags">
                      {getCategory(post) ? <span className={`tag ${visual.tagClass}`}>{getCategory(post)}</span> : null}
                    </div>

                    <div className="card-title">{post.title || "Без названия"}</div>

                    <div className="card-excerpt">
                      {post.description ||
                        "Краткий разбор психолога: как распознать состояние и что можно сделать уже сегодня."}
                    </div>

                    <div className="card-foot">
                      <span className="card-author">
                        {getAuthorUserId(post) ? (
                          <button
                            type="button"
                            className="b-psych-link-btn"
                            onClick={(event) => openPsychologistProfile(event, getAuthorUserId(post))}
                          >
                            {getInitials(getAuthorName(post))}
                          </button>
                        ) : (
                          getInitials(getAuthorName(post))
                        )}{" "}
                        · {getReadingMinutes(post, 6)} мин
                      </span>

                      <div className="card-stats">
                        <button
                          type="button"
                          className={`card-like${liked ? " liked" : ""}`}
                          onClick={(event) => onToggleLike(event, post, likeKey, fallbackLikes)}
                          disabled={!!likePendingMap[likeKey]}
                          aria-label="Лайк"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>{likes}</span>
                        </button>
                        <span>💬 {getCommentsCount(post, idx + 1)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">Ничего не найдено{q ? ` по запросу «${q}».` : "."}</div>
        )}

        {canLoadMore ? (
          <div className="load-more">
            <button className="load-more-btn" type="button" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? "Загрузка…" : "Показать ещё статьи"}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        ) : null}

        <div className="bottom-grid">
          <div className="sbar-card nl-card">
            <div className="sbar-lbl">Рассылка</div>
            <div className="nl-title">Раз в неделю — самое важное</div>
            <div className="nl-sub">Лучшие статьи, новые авторы и практики прямо в почту.</div>

            <form onSubmit={onNewsletterSubmit}>
              <input
                className="nl-input"
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Ваш email…"
              />
              <button className="nl-btn" type="submit">
                Подписаться на рассылку →
              </button>
            </form>
          </div>

          <div className="sbar-card">
            <div className="sbar-lbl">Популярные авторы</div>
            {popularAuthors.map((author) => (
              <div key={`${author.userId || "n"}-${author.name}`} className="pa-row">
                <div className="pa-av" style={{ background: author.avatar }}>
                  {author.initials}
                </div>
                <div className="pa-info">
                  <div className="pa-name">
                    {author.userId ? (
                      <button
                        type="button"
                        className="b-psych-link-btn"
                        onClick={(event) => openPsychologistProfile(event, author.userId)}
                      >
                        {author.name}
                      </button>
                    ) : (
                      author.name
                    )}
                  </div>
                  <div className="pa-posts">{author.postsCount} постов</div>
                </div>
                <button
                  className="pa-follow"
                  type="button"
                  onClick={() => onToggleFollow(author)}
                  disabled={!!followPendingMap[authorKey(author)]}
                >
                  {followedMap[authorKey(author)] ? "✓ Вы подписаны" : "+ Следить"}
                </button>
              </div>
            ))}
          </div>

          <div className="sbar-card">
            <div className="sbar-lbl">Популярные темы</div>
            <div className="trends">
              {trendingTags.map((tag) => (
                <button key={tag} type="button" className="trend-tag" onClick={() => onTrendTagClick(tag)}>
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className={`${isAuthenticated ? "podcasts-hub " : ""}b-blog-v2${isAuthenticated ? " b-blog-v2--auth" : ""}`}>
      <Helmet prioritizeSeoTags>
        <title>Блог о психологии и терапии | Бюро Души</title>
        <meta
          name="description"
          content="Статьи о психологии, терапии и выборе специалиста: советы, разборы, практики и ответы на частые вопросы."
        />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta name="robots" content={robots} />
        {listSchemas.map((obj, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
      </Helmet>

      {isAuthenticated ? (
        <div className="podcasts-shell">
          <AppSidebarV2
            activeKey="blog"
            mobileOpen={sidebarOpen}
            onCloseMobile={() => setSidebarOpen(false)}
          />

          <button
            className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
            type="button"
            aria-label="Закрыть меню"
            onClick={() => setSidebarOpen(false)}
          />

          <div className="podcasts-main">
            <header className="podcasts-topbar blog-auth-topbar">
              <div className="podcasts-topbar__left blog-auth-topbar__left">
                <button
                  className="podcasts-topbar__menu-btn"
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Открыть меню"
                >
                  ☰
                </button>
                <div className="blog-auth-topbar__title">Блог</div>
                <form className="podcasts-topbar__search blog-auth-topbar__search" onSubmit={applySearch}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                    <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <input
                    type="text"
                    value={queryDraft}
                    onChange={(e) => setQueryDraft(e.target.value)}
                    placeholder="Тема, автор, ключевое слово…"
                  />
                </form>
              </div>

              <div className="podcasts-topbar__right blog-auth-topbar__right">
                {canManageBlog ? (
                  <button
                    className="podcasts-btn podcasts-btn--mint blog-auth-topbar__create-btn"
                    type="button"
                    onClick={() => navigate("/dashboard/blog/new")}
                  >
                    Написать статью
                  </button>
                ) : null}
                <button
                  className="podcasts-topbar__avatar blog-auth-topbar__avatar"
                  type="button"
                  onClick={() => navigate("/dashboard/settings")}
                >
                  {getInitials(me?.name || me?.login || "Бюро Души")}
                </button>
              </div>
            </header>

            {pageBody}
            <MobileQuickNav items={blogQuickNavItems} />
          </div>
        </div>
      ) : (
        pageBody
      )}

      <div className={`blog-toast${toastShow ? " show" : ""}`}>{toastText}</div>
    </div>
  );
}
